import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const giftVoucherAttributeKey = '_gift_voucher_code';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Set<string>} */
  #pendingGiftAdds = new Set();

  /** @type {Array<{discount_code: string, product_variant: string, use_code_prefix?: boolean, minimum_amount?: string, gift_minimum_amount?: string, first_time_buyer_only?: boolean | string, add_automatically?: boolean | string}>} */
  #gwpDiscountMappings = [];

  /** @type {MutationObserver | null} */
  #gwpDatasetObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateGwpDiscountMappings();
    this.#observeGwpDiscountDataset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gwpDatasetObserver?.disconnect();
    this.#gwpDatasetObserver = null;
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        /** @type {string[]} */
        const codes = [];
        Array.from(discountCodes).forEach((element) => {
          if (!(element instanceof HTMLLIElement)) return;
          if (typeof element.dataset.discountCode !== 'string') return;
          codes.push(element.dataset.discountCode);
        });
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      const applicableDiscountCodes = Array.isArray(data.discount_codes)
        ? data.discount_codes
          .filter((/** @type {{ code?: string, applicable?: boolean }} */ discount) => discount?.applicable === true)
          .map((/** @type {{ code?: string }} */ discount) => discount.code)
          .filter((/** @type {string | undefined} */ code) => typeof code === 'string')
        : [];
      const { missingGifts, discountCodesToRemove } = this.#findMissingGiftsForDiscountCodes(applicableDiscountCodes, data);

      let latestSectionHtml = newHtml;
      if (discountCodesToRemove.length > 0) {
        const dataAfterDiscountRemoval = await this.#removeDiscountCodes(discountCodesToRemove, data);
        const removedSectionHtml = dataAfterDiscountRemoval?.sections?.[this.dataset.sectionId];
        if (typeof removedSectionHtml === 'string') {
          latestSectionHtml = removedSectionHtml;
        }
      }

      if (missingGifts.length > 0) {
        for (const gift of missingGifts) {
          const inFlightKey = `${gift.variantId}::${gift.appliedCode.toLowerCase()}`;
          if (this.#pendingGiftAdds.has(inFlightKey)) continue;

          this.#pendingGiftAdds.add(inFlightKey);
          try {
            const addResponse = await this.#addGiftProduct(gift);
            const addedSectionHtml = addResponse?.sections?.[this.dataset.sectionId];
            if (typeof addedSectionHtml === 'string') {
              latestSectionHtml = addedSectionHtml;
            }
          } finally {
            this.#pendingGiftAdds.delete(inFlightKey);
          }
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, latestSectionHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Parses GWP discount mappings once and removes raw data attribute from the DOM.
   */
  #hydrateGwpDiscountMappings() {
    const raw = this.dataset.gwpDiscounts;
    if (!raw) {
      this.#gwpDiscountMappings = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      this.#gwpDiscountMappings = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      this.#gwpDiscountMappings = [];
    }

    this.removeAttribute('data-gwp-discounts');
  }

  /**
   * Ensures re-rendered markup cannot leave raw GWP mapping data on the element.
   */
  #observeGwpDiscountDataset() {
    this.#gwpDatasetObserver?.disconnect();

    this.#gwpDatasetObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') continue;
        if (mutation.attributeName !== 'data-gwp-discounts') continue;
        if (!this.hasAttribute('data-gwp-discounts')) continue;

        this.#hydrateGwpDiscountMappings();
      }
    });

    this.#gwpDatasetObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-gwp-discounts'],
    });
  }

  /**
   * Returns parsed GWP discount mappings from dataset.
   * @returns {Array<{discount_code: string, product_variant: string, use_code_prefix?: boolean, minimum_amount?: string, gift_minimum_amount?: string, first_time_buyer_only?: boolean | string, add_automatically?: boolean | string}>}
   */
  #gwpDiscounts() {
    return this.#gwpDiscountMappings;
  }

  /**
   * @param {string} inputCode
   * @param {{discount_code?: string, use_code_prefix?: boolean}} mapping
   */
  #matchesDiscountCode(inputCode, mapping) {
    if (inputCode == mapping.discount_code) return true;

    return mapping.use_code_prefix === true && inputCode.startsWith(String(mapping.discount_code));
  }

  /**
   * @param {unknown} value
   * @returns {boolean}
   */
  #isTruthy(value) {
    return String(value ?? '').trim().toLowerCase() === 'true';
  }

  /**
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  #toThreshold(value, fallback = 0) {
    const parsed = parseFloat(String(value ?? ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * Returns whether current buyer is first-time based on data attribute.
   * Missing value defaults to true (guest behavior parity with extension logic).
   * @returns {boolean}
   */
  #isFirstTimeCustomer() {
    const value = this.dataset.firstTimeCustomer;
    return value?.toLowerCase() == 'true';
  }

  /**
   * Removes one or more discount codes by updating cart discounts directly.
   * This avoids relying on discount pills being present in the current DOM.
   *
   * @param {string[]} codesToRemove
   * @param {{ discount_codes?: Array<{ code?: string, applicable?: boolean }> }} data
   * @returns {Promise<any | null>}
   */
  async #removeDiscountCodes(codesToRemove, data) {
    if (codesToRemove.length === 0 || typeof this.dataset.sectionId !== 'string') {
      return null;
    }

    const normalizedCodesToRemove = new Set(codesToRemove.map((code) => code.trim().toLowerCase()));

    const responseCodes = Array.isArray(data?.discount_codes)
      ? data.discount_codes
        .map((/** @type {{ code?: string }} */ discount) => discount?.code)
        .filter((/** @type {string | undefined} */ code) => typeof code === 'string')
      : [];

    const currentDiscountCodes = responseCodes.length > 0 ? responseCodes : this.#existingDiscounts();
    const nextDiscountCodes = currentDiscountCodes.filter(
      (code) => !normalizedCodesToRemove.has(code.trim().toLowerCase())
    );

    if (nextDiscountCodes.length === currentDiscountCodes.length) {
      return null;
    }

    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: nextDiscountCodes.join(','),
        sections: [this.dataset.sectionId],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    return response.json();
  }

  /**
   * @param {string[]} discountCodes
   * @param {{ items?: Array<Record<string, any>>, total_price?: number }} data
     * @returns {{ missingGifts: Array<{variantId: number, appliedCode: string}>, discountCodesToRemove: string[] }}
   */
  #findMissingGiftsForDiscountCodes(discountCodes, data) {
      if (!Array.isArray(discountCodes) || discountCodes.length === 0) {
        return { missingGifts: [], discountCodesToRemove: [] };
      }

    const cartTotal = typeof data?.total_price === 'number' ? data.total_price / 100 : null;
    const mappings = this.#gwpDiscounts();
    const items = Array.isArray(data?.items) ? data.items : [];

    /** @type {Array<{variantId: number, appliedCode: string}>} */
    const missingGifts = [];
    /** @type {string[]} */
    const discountCodesToRemove = [];
    const seenKeys = new Set();

    for (const inputCode of discountCodes) {
      const mapping = mappings.find((entry) => this.#matchesDiscountCode(inputCode, entry));

      if (!mapping) continue;

      const { product_variant, minimum_amount, gift_minimum_amount, first_time_buyer_only, add_automatically } = mapping;

      const requiresFirstTimeCustomer = this.#isTruthy(first_time_buyer_only);
      if (requiresFirstTimeCustomer && !this.#isFirstTimeCustomer()) {
        continue;
      }

      const minimumAmountThreshold = this.#toThreshold(minimum_amount, 0);
      const goodieThreshold = this.#toThreshold(gift_minimum_amount, 0);
      const addAutomatically = this.#isTruthy(add_automatically);

      if (cartTotal !== null && cartTotal < minimumAmountThreshold) {
        discountCodesToRemove.push(inputCode);
        continue;
      }

      if (cartTotal !== null && cartTotal < goodieThreshold) {
        continue;
      }

      if (!addAutomatically) {
        continue;
      }

      const alreadyInCart = items.find((item) => {
        const attrs = item?.properties || {};
        const itemVariantId = String(item?.id || item?.variant_id || item?.variantId || '');
        return this.#normalizeVariantId(itemVariantId) === this.#normalizeVariantId(product_variant)
          && typeof attrs[giftVoucherAttributeKey] === 'string'
          && attrs[giftVoucherAttributeKey].trim().toLowerCase() === inputCode.toLowerCase();
      });

      if (alreadyInCart) continue;

      const giftKey = `${this.#normalizeVariantId(product_variant)}::${inputCode.toLowerCase()}`;
      if (seenKeys.has(giftKey)) continue;

      seenKeys.add(giftKey);
      missingGifts.push({
        variantId: parseInt(this.#normalizeVariantId(product_variant), 10),
        appliedCode: inputCode
      });
    }

    return {
      missingGifts,
      discountCodesToRemove,
    };
  }

  /**
   * Normalizes variant IDs from numeric or gid format to comparable numeric string.
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeVariantId(value) {
    if (value == null) return '';
    const str = String(value).trim();
    if (str === '') return '';
    return str.replace('gid://shopify/ProductVariant/', '');
  }

  /**
  * Adds the matching gift product to cart.
  * @param {{variantId: number, appliedCode: string}} gift
   */
  async #addGiftProduct(gift) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.dataset.sectionId].filter(Boolean));
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      items: [
        {
          id: gift.variantId,
          quantity: 1,
          properties: {
            [giftVoucherAttributeKey]: gift.appliedCode          },
          }
        ],
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    return response.json();
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
